import { useMemo, useState } from "react";
import { Search, Store } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SHOP } from "@/lib/shop";
import RESELLERS from "@/lib/resellers.json";
import { SOCIALS } from "@/components/site/SocialLinks";
import { Eyebrow, btnDark, btnLight } from "@/components/ambassadors/ui";
import { ResellerCard } from "@/components/resellers/ResellerCard";
import { ZONES } from "@/pages/LocalResellers";
import { Link } from "react-router-dom";

const ZONE_BY_DEPT = Object.fromEntries(ZONES.map((z) => [z.dept, z]));

const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const DEPTS = [...new Map(RESELLERS.map((r) => [r.dept, r.dept_name])).entries()].sort((a, b) => a[0].localeCompare(b[0]));

function Filters({ q, setQ, dept, setDept }) {
  const chip = (active) => `rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${active ? "border-[#141013] bg-[#141013] text-[#FBF8F3]" : "border-[#D9C8B3] bg-white text-[#141013] hover:border-[#7A5418]"}`;
  return (
    <div>
      <label className="flex items-center gap-3 rounded-full border border-[#D9C8B3] bg-white px-5 focus-within:border-[#7A5418]">
        <Search className="h-4 w-4 shrink-0 text-[#7A5418]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ville, code postal ou nom du magasin" data-testid="resellers-search"
          className="h-12 w-full bg-transparent text-base text-[#141013] outline-none placeholder:text-[#7D7067]" />
      </label>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible">
        <button onClick={() => setDept("")} data-testid="resellers-dept-all" className={`shrink-0 ${chip(!dept)}`}>Tous</button>
        {DEPTS.map(([code, name]) => (
          <button key={code} onClick={() => setDept(code === dept ? "" : code)} data-testid={`resellers-dept-${code}`} className={`shrink-0 ${chip(code === dept)}`}>{code} · {name}</button>
        ))}
      </div>
    </div>
  );
}

function BecomeReseller() {
  return (
    <section data-testid="resellers-become" className="mt-20 grid gap-8 rounded-3xl bg-[#141013] p-8 text-[#FBF8F3] md:grid-cols-[1.4fr_1fr] md:items-center md:p-12">
      <div>
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-[#E2C07E]">Professionnels</p>
        <h2 className="font-display text-3xl md:text-4xl">Devenir revendeur Ozentya</h2>
        <p className="mt-4 text-sm leading-relaxed text-[#E8DED2] md:text-base">Salon de coiffure, boutique afro ou institut : proposez les soins Ozentya à votre clientèle. Contactez-nous ou découvrez nos packs achat-revente.</p>
      </div>
      <div className="flex flex-col gap-3">
        <a href={SOCIALS.pro} target="_blank" rel="noopener noreferrer" data-testid="resellers-pro-link" className={`${btnDark} bg-[#E2C07E] text-[#141013] hover:bg-[#F1DBA6]`}>Pack Pro · Achat-revente</a>
        <a href={`tel:${SHOP.tel}`} data-testid="resellers-call" className={`${btnLight} border-[#FBF8F3] text-[#FBF8F3] hover:bg-[#FBF8F3] hover:text-[#141013]`}>Appeler le {SHOP.phone}</a>
      </div>
    </section>
  );
}

export default function Resellers() {
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  useSeo({
    title: `Revendeurs Ozentya : ${RESELLERS.length} salons et boutiques afro`,
    description: "Trouvez un revendeur Ozentya près de chez vous : salons de coiffure et boutiques afro en Île-de-France, à Marseille, Lyon, Montpellier, aux Antilles, en Guyane et au Sénégal.",
    path: "/revendeurs",
  });
  const list = useMemo(() => {
    const n = norm(q.trim());
    return RESELLERS.filter((r) => !r.featured && (!dept || r.dept === dept) && (!n || norm(`${r.name} ${r.address} ${r.zip} ${r.city}`).includes(n)));
  }, [q, dept]);
  const groups = DEPTS.map(([code, name]) => [code, name, list.filter((r) => r.dept === code)]).filter(([, , items]) => items.length);
  return (
    <main data-testid="resellers-page" className="bg-[#F6F0E7] px-5 pb-24 pt-32 md:px-10 md:pt-40">
      <div className="mx-auto max-w-6xl">
        <Eyebrow>Points de vente</Eyebrow>
        <h1 className="font-display text-4xl leading-[1.05] text-[#141013] sm:text-5xl lg:text-6xl">Nos revendeurs <span className="italic text-[#7A5418]">Ozentya</span></h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#5E544D]">Retrouvez nos soins chez {RESELLERS.length} salons, boutiques et magasins afro en France métropolitaine, en Guadeloupe, en Martinique, en Guyane et au Sénégal. Les stocks varient selon les magasins : appelez avant de vous déplacer.</p>
        <p className="mt-3 flex items-center gap-2 text-sm text-[#5E544D]"><Store className="h-4 w-4 text-[#7A5418]" /> Vous préférez commander en ligne ? Livraison offerte en France métropolitaine via Mondial Relay.</p>
        <ul data-testid="resellers-featured" className="mt-10 grid gap-4 md:grid-cols-2">{RESELLERS.filter((r) => r.featured).map((r) => <ResellerCard key={r.id} r={r} />)}</ul>
        <div className="mt-10"><Filters q={q} setQ={setQ} dept={dept} setDept={setDept} /></div>
        <p data-testid="resellers-count" className="mt-6 text-sm text-[#5E544D]">{list.length} revendeur{list.length > 1 ? "s" : ""}</p>
        {groups.map(([code, name, items]) => (
          <section key={code} data-testid={`resellers-group-${code}`} className="mt-10">
            <h2 className="font-display text-2xl text-[#141013]">{name} <span className="text-[#7A5418]">({code})</span></h2>
            {ZONE_BY_DEPT[code] ? <Link to={ZONE_BY_DEPT[code].path} data-testid={`resellers-zone-link-${code}`} className="mt-1 inline-block text-xs font-semibold text-[#7A5418] underline underline-offset-4">Où acheter Ozentya {ZONE_BY_DEPT[code].prep}</Link> : null}
            <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{items.map((r) => <ResellerCard key={r.id} r={r} />)}</ul>
          </section>
        ))}
        {!list.length ? <p data-testid="resellers-empty" className="mt-10 rounded-2xl bg-white p-6 text-sm text-[#5E544D]">Aucun revendeur ne correspond à votre recherche. Commandez en ligne ou appelez-nous au {SHOP.phone}.</p> : null}
        <BecomeReseller />
      </div>
    </main>
  );
}
